import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: React.ReactNode;
    children: React.ReactNode;
    maxWidth?: string;
    className?: string;
    allowBackgroundScroll?: boolean;
    compact?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'max-w-md',
    className = '',
    allowBackgroundScroll = false,
    compact = false
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            if (!allowBackgroundScroll) {
                document.body.style.overflow = 'hidden';
            }
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose, allowBackgroundScroll]);

    if (!isOpen) return null;

    const headerPadding = compact ? 'px-4 sm:px-6 py-3 sm:py-4' : 'px-4 sm:px-6 py-3.5 sm:py-4';
    const titleSize = compact ? 'text-xl font-extrabold text-gold-400 tracking-tight' : 'text-2xl font-black text-gold-400 tracking-tight';
    const closeBtnPadding = compact ? 'p-1.5' : 'p-2';
    const closeBtnIconSize = compact ? 20 : 24;
    const bodyPadding = compact ? 'px-6 pt-4 pb-5' : 'p-8';

    return createPortal(
        <div
            data-no-swipe="true"
            className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className={`relative w-full ${maxWidth} bg-mystic-1100/95 backdrop-blur-3xl border border-gold-500/20 rounded-[2rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] transform transition-all duration-300 scale-100 flex flex-col max-h-[90vh] ${className} animate-in zoom-in-95 duration-300 overflow-hidden`}
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between ${headerPadding} border-b border-white/5 bg-white/[0.02]`} dir="rtl">
                    <h3 className={titleSize}>{title}</h3>
                    <button
                        onClick={onClose}
                        className={`${closeBtnPadding} text-mystic-400 hover:text-white transition-all rounded-full hover:bg-white/5 active:scale-95 shrink-0 -ms-1 sm:-ms-2`}
                    >
                        <X size={closeBtnIconSize} />
                    </button>
                </div>
                <div className="overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent flex-1">
                    <div className={bodyPadding}>
                        {children}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
